import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { ValueLabel } from "gematik-form-library";
import { Subscription } from "rxjs";
import { KpiBaseChartDto } from "../../../../services/chart/kpi-chart.service";
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexYAxis,
  ApexDataLabels,
  ApexGrid,
  ApexStroke,
  ApexTitleSubtitle,
  ApexPlotOptions,
} from "ng-apexcharts";

export type StaticChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis[];
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  stroke: ApexStroke;
  title: ApexTitleSubtitle;
  colors: string[];
  plotOptions: ApexPlotOptions;
};

@Component({
  selector: "users-per-frequency-chart",
  styleUrls: ["users-per-frequency-chart.component.scss"],
  standalone: false,
  template: `
    <div>
      <div class="d-flex align-items-center">
        <strong class="me-2">{{ "portal.charts.users.frequency.title" | translate }}</strong>
        <gem-radio-button-group
          [control]="frequency"
          [valueLabels]="frequencyOptions"
          (change)="onFrequencyChange($event)"
        ></gem-radio-button-group>
      </div>
      <div class="mt-4">
        <apx-chart
          [series]="chartOptions.series"
          [chart]="chartOptions.chart"
          [xaxis]="chartOptions.xaxis"
          [yaxis]="chartOptions.yaxis"
          [dataLabels]="chartOptions.dataLabels"
          [grid]="chartOptions.grid"
          [stroke]="chartOptions.stroke"
          [title]="chartOptions.title"
          [plotOptions]="chartOptions.plotOptions"
          [colors]="chartOptions.colors"
        ></apx-chart>
      </div>
    </div>
  `,
})
export class UsersPerFrequencyChartComponent implements OnInit, OnDestroy {
  users: any[] = [];
  runningTotal: any[] = [];

  @Input() set chart(chart: KpiBaseChartDto) {
    if (chart && chart.data) {
      this.users = [];
      this.runningTotal = [];
      chart.data.forEach((d) => {
        this.users.push({
          x: d.label,
          y: d.value,
        });
        this.runningTotal.push({
          x: d.label,
          y: d.runningTotal,
        });
      });
      this.setChartSeries();
    }
  }
  @Input() set frequency(frequency: string) {
    if (frequency) {
      this.frequency.setValue(frequency);
    }
  }
  @Output() frequencyChange: EventEmitter<string> = new EventEmitter();

  public chartOptions: Partial<StaticChartOptions>;

  form: FormGroup = this.fb.group({
    frequency: ["daily"],
  });

  frequencyOptions: ValueLabel[] = [
    {
      value: "daily",
      label: "portal.charts.users.frequency.labels.perDay",
    },
    { value: "monthly", label: "portal.charts.users.frequency.labels.perMonth" },
  ];

  subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private translateService: TranslateService,
  ) {
    this.chartOptions = {
      series: [],
      plotOptions: {
        bar: {
          columnWidth: "20%",
        },
      },
      chart: {
        height: 200,
        type: "line",
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "straight",
        width: [0, 2],
      },
      title: {},
      colors: ["var(--gematik-red-medium)", "var(--gematik-blue)"],
      grid: {
        row: {
          opacity: 0.5,
        },
      },
      xaxis: {
        categories: [],
      },
      yaxis: [
        {
          showAlways: true,
          opposite: false,
        },
        {
          showAlways: true,
          opposite: true,
        },
      ],
    };
  }

  ngOnInit(): void {
    const langSub = this.translateService.onLangChange.subscribe(() => {
      this.setChartSeries();
    });
    this.subscriptions.push(langSub);
  }

  onFrequencyChange(e): void {
    this.frequencyChange.emit(e);
  }

  get frequency(): FormControl {
    return this.form.get("frequency") as FormControl;
  }

  private setChartSeries(): void {
    this.chartOptions.series = [
      {
        type: "column",
        name: this.translateService.instant("portal.charts.users.frequency.labels.users"),
        data: [...this.users],
      },
      {
        name: this.translateService.instant("portal.charts.users.frequency.labels.runningTotal"),
        data: [...this.runningTotal],
      },
    ];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
